import { useAppSelector } from 'app/hooks';
import { ExistingSystemType } from 'data/types';
import { SystemRecommendationSelectionsEntry } from 'features/structure/structure-slice';
import useIsDuctedOnlyPathway from 'hooks/use-is-ducted-only-pathway';
import { useMemo } from 'react';
import { isRecommendationStripCompleted } from 'utils/existing-equipment';
import {
  CalcTonnage,
  calcTonnageFromBtu,
  deriveEquipmentTonnage,
  EffectiveTonnage,
  EquipmentTonnage,
  resolveEffectiveTonnage,
  TonnageRecord
} from 'utils/tonnage-logic';
import { findAnchorDuctedSystem } from 'utils/tonnage-seed-state';

export type StructureTonnage = {
  // The ANCHOR system for the per-structure tonnage surfaces (skip routing,
  // L&S conflict dialog, CS filter seed): the first ducted system. The
  // ducted tile flow has exactly one (proposal §E — per-system IS
  // per-structure in practice); multi-system structures anchor on the first.
  system: ExistingSystemType | null;
  entry: SystemRecommendationSelectionsEntry | null;
  record: TonnageRecord | null;
  equipment: EquipmentTonnage | null;
  calc: CalcTonnage | null;
  effective: EffectiveTonnage | null;
  // False until the structure-load hydration lands: acting on the
  // unhydrated mirror could wipe a saved record (the PATCH replaces whole
  // records) or seed a filter a persisted dismissal opted out of.
  loaded: boolean;
};

// Reactive per-structure view of the AHA-6681 tonnage lifecycle for
// consumers OUTSIDE a specific strip (EE footer, header stepper, L&S
// dialog, CS banner). The per-system tile row in the want card computes the
// same pieces locally from its own system prop.
export default function useStructureTonnage(): StructureTonnage {
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const loaded = useAppSelector(
    (state) => state.structure.recommendationSelectionsLoaded
  );
  const coolingBtu = useAppSelector((state) => state.project.totalBTUs.cooling);

  return useMemo(() => {
    const system = findAnchorDuctedSystem({
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    });
    const entry = system
      ? recommendationSelectionsBySystem[system.uniqueId] ?? null
      : null;
    const record = entry?.tonnage ?? null;
    const equipment = system ? deriveEquipmentTonnage(system.components) : null;
    const calc = calcTonnageFromBtu(coolingBtu);
    const effective = system
      ? resolveEffectiveTonnage({ record, equipment, calc })
      : null;
    return { system, entry, record, equipment, calc, effective, loaded };
  }, [
    existingSystems,
    recommendationSelectionsBySystem,
    wantCompositionPicksBySystem,
    coolingBtu,
    loaded
  ]);
}

// The §F skip-eligibility gate: STRICT ducted-only preference ('Help Me
// Decide' never arms it) ∧ an effective tonnage ∧ every system's strip
// completed via the SHARED predicate (the same one the page-progress
// watcher's EE gate and Choose Systems eligibility read — no fork). Drives
// the presentational 'Optional' stepper badges only — zero page-progress
// semantics ride on it (the EE footer keys on the Unsure answer instead,
// useSizeSkipArmed).
export function useTonnageSkipEligible(): boolean {
  const ductedOnly = useIsDuctedOnlyPathway();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const { effective, loaded } = useStructureTonnage();

  return (
    ductedOnly &&
    loaded &&
    effective !== null &&
    existingSystems.length > 0 &&
    existingSystems.every((system) =>
      isRecommendationStripCompleted(
        recommendationSelectionsBySystem[system.uniqueId],
        wantCompositionPicksBySystem[system.uniqueId]
      )
    )
  );
}
